import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import { VitalDataMock, VitalResMock } from '../types/vital.type'

type VitalArg = {
  patientId?: string
  vitalId?: string
  query?: string
  limit?: number
  page?: string
  captured_date?: string
  sortDir?: string
  intakeId?: string
  encounterId?: string
  include_history?: boolean
}
export const useVitals = (arg?: VitalArg) => {
  const patientId = arg?.patientId
  const vitalId = arg?.vitalId
  const query = arg?.query
  const limit = arg?.limit || 150
  const page = arg?.page || '1'
  const captured_date = arg?.captured_date
  const sortDir = arg?.sortDir || 'desc'
  const intakeId = arg?.intakeId
  const encounterId = arg?.encounterId
  const include_history = arg?.include_history || false

  const { data: response, isLoading: vitalsIsLoading } = useQuery({
    queryFn: async () => {
      let endpoint = `/vital_signs?`
      if (sortDir) endpoint += `sort_dir=${sortDir}&`
      if (query) endpoint += `query=${query}&`
      if (limit) endpoint += `limit=${limit}&`
      if (intakeId) endpoint += `intake_id=${intakeId}&`
      if (page) endpoint += `page%5Bnumber%5D=${page}&page%5Bsize%5D=${limit}&`
      if (captured_date) endpoint += `captured_date=${captured_date}&`
      if (patientId) endpoint += `patient_id=${patientId}&`
      if (encounterId) endpoint += `encounter_id=${encounterId}&`
      if (include_history) endpoint += `include_history=${include_history}&`
      endpoint = endpoint.replace(/[&?]$/, '')
      const { data } = await AXIOS_INSTANCE.get<VitalResMock<VitalDataMock[]>>(endpoint)
      return data
    },
    queryKey: [
      'vital_signs',
      { captured_date, encounterId, intakeId, limit, page, patientId, query, include_history, sortDir }
    ]
  })

  const { mutateAsync: addVitals } = useMutation({
    mutationFn: async (formVal: any) => {
      const { data } = await AXIOS_INSTANCE.post(`/vital_signs`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vital_signs'] })
      queryClient.invalidateQueries({ queryKey: ['intake'] })
    }
  })

  const { mutateAsync: updateVitals } = useMutation({
    mutationFn: async ({ formVal, vitalId }: { formVal: any; vitalId: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(`/vital_signs/${vitalId}`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vital_signs', { vitalId }] })
  })

  const { mutateAsync: correctVitals } = useMutation({
    mutationFn: async (formVal: any) => {
      const { data } = await AXIOS_INSTANCE.post(`/vital_signs/correct`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vital_signs'] })
  })

  const { mutateAsync: deleteVitals } = useMutation({
    mutationFn: async (vitalId: string) => {
      await AXIOS_INSTANCE.delete(`/vital_signs/${vitalId}`)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vital_signs'] })
  })

  return {
    addVitals,
    correctVitals,
    deleteVitals,
    pagination: response?.meta?.pagination,
    updateVitals,
    vitals: response?.data,
    vitalsIsLoading
  }
}
